/* fender-lookup.js
   Full Fender serial detection engine (Option A)
   Target IDs on page:
     - input:   #fender-serial-input
     - button:  #fenderLookupBtn
     - output:  #fender-lookup-result
   Author: Garrett Park Guitars — engineered for shop use
*/

(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  function el(id) { return document.getElementById(id); }
  function safeHTML(s){ if(s==null) return ''; return String(s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }
  function normalize(sn){ return (sn||'').toString().toUpperCase().trim().replace(/\s+/g,''); }
  function twoDigitYearToFull(n){
    n = Number(n);
    if (isNaN(n)) return null;
    // Heuristic: 00-30 => 2000s, else 1900s
    return (n <= 30) ? 2000 + n : 1900 + n;
  }
  function addCandidate(arr, label, confidence, why){
    arr.push({label: label, confidence: confidence || 'low', why: why || ''});
  }

  /* ---------- Known range tables (from your page) ---------- */

  const vintageRanges = [ // 1954-1963 numeric neckplate
    {min:1, max:7000, year:1954},
    {min:7000, max:9000, year:1955},
    {min:9000, max:17000, year:1956},
    {min:17000, max:25000, year:1957},
    {min:25000, max:34000, year:1958},
    {min:34000, max:44000, year:1959},
    {min:44000, max:59000, year:1960},
    {min:59000, max:71000, year:1961},
    {min:71000, max:93000, year:1962},
    {min:93000, max:99999, year:1963}
  ];

  const sixDigitRanges = [ // 1965-1976 mapping
    {min:100000, max:110000, note:'late 1965'},
    {min:110000, max:200000, note:'1966'},
    {min:200000, max:210000, note:'1967'},
    {min:210000, max:250000, note:'1968'},
    {min:250000, max:280000, note:'1969'},
    {min:280000, max:300000, note:'1970'},
    {min:300000, max:340000, note:'1971'},
    {min:340000, max:370000, note:'1972'},
    {min:370000, max:520000, note:'1973'},
    {min:500000, max:580000, note:'1974'},
    {min:580000, max:690000, note:'1975'},
    {min:690000, max:750000, note:'1976'}
  ];

  /* ---------- Helper: build HTML output ---------- */
  function buildCompactHeader(best, others){
    if(!best) return '';
    const badge = `<span class="fender-badge">${safeHTML(best.confidence.toUpperCase())}</span>`;
    const otherText = (others && others.length) ? `<div style="font-size:0.95rem;color:rgba(0,0,0,0.7);margin-top:6px">Other possibilities: ${others.map(o=>safeHTML(o.label)).join('; ')}</div>` : '';
    return `
      <div class="fender-compact" role="region" aria-label="Best guess">
        <div>
          <div class="fender-best">${safeHTML(best.label)}</div>
          ${otherText}
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          ${badge}
          <button class="fender-details-toggle" type="button" aria-expanded="false">Show details</button>
        </div>
      </div>
    `;
  }

  function formatCandidatesHTML(candidates){
    if(!Array.isArray(candidates) || candidates.length===0){
      return `<div><strong>No result</strong></div>`;
    }
    const best = candidates[0];
    const others = candidates.slice(1,4); // show up to 3 others
    let html = '<div>';
    html += buildCompactHeader(best, others);
    html += `<div class="fender-collapse" id="fender-collapse">`;
    candidates.forEach(c => {
      html += `<div class="fender-candidate"><strong>${safeHTML(c.label)}</strong><div class="fender-why">${safeHTML(c.why)}</div></div>`;
    });
    html += `</div>`;
    html += `<div style="margin-top:8px;padding-top:8px;border-top:1px dashed rgba(0,0,0,0.06);color:rgba(0,0,0,0.7)">
      <strong>Tips to improve accuracy</strong>
      <ul style="margin:8px 0 0 18px;">
        <li>Look for neck-heel or body stamps with dates.</li>
        <li>Open the control cavity and check potentiometer date codes (often most accurate).</li>
        <li>Note headstock logo style, bridge type and finish to corroborate.</li>
      </ul></div>`;
    html += '</div>';
    return html;
  }

  /* ---------- Core detection function ---------- */
  function lookupFenderSerial(rawInput){
    const snRaw = rawInput || '';
    const sn = normalize(snRaw);
    const candidates = [];

    if(!sn){
      addCandidate(candidates, 'No serial provided', 'low', 'Please enter a serial number (example: L12345, S67890, MX1782345).');
      return candidates;
    }

    // 1) MX / US prefix with two-digit year (MX17 -> 2017)
    let m = sn.match(/^(MX|US)(\d{2,4})$/);
    if(m){
      const prefix = m[1];
      const digits = m[2];
      const yy = digits.slice(0,2);
      const year = twoDigitYearToFull(yy);
      addCandidate(candidates, `${prefix} prefix • Approx year: ${year}`, 'high', `Prefix '${prefix}' + two-digit year (${yy}) commonly denotes ${prefix==='MX'?'Ensenada/Mexico':'Corona/USA'} builds.`);
      return candidates;
    }

    // 2) MX with longer code (MX + 5/6 digits where year in middle/end) — heuristic
    m = sn.match(/^MX(\d{4,6})$/);
    if(m){
      const rest = m[1];
      const maybeYear = rest.length >= 4 ? rest.slice(0,2) : null;
      const y = maybeYear ? twoDigitYearToFull(maybeYear) : null;
      addCandidate(candidates, `Made in Mexico • pattern MX${rest}`, 'medium', y ? `Likely around ${y} (heuristic)` : 'Mexico Ensenada pattern; check headstock label for year.');
      return candidates;
    }

    // 3) US prefix longer codes
    m = sn.match(/^US(\d{4,6})$/);
    if(m){
      addCandidate(candidates, `Made in USA • pattern ${sn}`, 'medium', 'US/Corona patterns vary — look at the next two digits for a year marker where present.');
      return candidates;
    }

    // 4) S-prefix vintage (S6..S9 etc) => 1970s
    if(/^S[6-9]\d{3,5}$/.test(sn) || /^S[6-9]$/.test(sn)) {
      const dec = sn.charAt(1);
      const year = 1970 + Number(dec);
      addCandidate(candidates, `${year} era (S-prefix)`, 'medium', `S-prefix with decade digit ${dec} typically indicates mid-late 1970s headstock serials.`);
      return candidates;
    }

    // 5) L-series e.g., L12345
    if(/^L\d{5}$/.test(sn)){
      const num = parseInt(sn.slice(1),10);
      let yearGuess = '1963–1965';
      if(num >= 1 && num <= 20000) yearGuess = 1963;
      else if(num > 20000 && num <= 59000) yearGuess = 1964;
      else if(num > 59000) yearGuess = 1965;
      addCandidate(candidates, `${sn} → L-series • ${yearGuess}`, 'high', 'L-prefixed five-digit neck-plate runs are strongly associated with 1963–1965 Fender production.');
      return candidates;
    }

    // 6) Pure numeric early 3-5 digits (1950s–early 1960s)
    if(/^\d{3,5}$/.test(sn)){
      const n = parseInt(sn,10);
      for(let r of vintageRanges){
        if(n >= r.min && n <= r.max){
          addCandidate(candidates, `Approx year: ${r.year}`, 'medium', `Numeric neck-plate serial ${n} sits inside ${r.min}–${r.max}, matching ${r.year} ranges.`);
          addCandidate(candidates, `${r.year - 1}–${r.year + 1} (possible overlap)`, 'low', 'Ranges sometimes overlap between models and years — corroborate with pot codes and features.');
          return candidates;
        }
      }
      // fallback for small numbers (early 50s)
      addCandidate(candidates, 'Early 1950s-era numeric serial (model-dependent)', 'low', 'Short numeric stamps often vary by model — use additional clues.');
      return candidates;
    }

    // 7) 6-digit numeric ranges (Norlin era 1965–1976)
    if(/^\d{6}$/.test(sn)){
      const n = parseInt(sn,10);
      for(let r of sixDigitRanges){
        if(n >= r.min && n <= r.max){
          addCandidate(candidates, `${r.note} (serial ${n})`, 'medium', `Serial ${n} falls within ${r.min}–${r.max} mapping to ${r.note}. Confirm with F-stamp and pot codes.`);
          return candidates;
        }
      }
      addCandidate(candidates, '1970s-era six-digit serial (approx)', 'low', '6-digit blocks were used across the Norlin years; pot codes often provide precision.');
      return candidates;
    }

    // 8) Prefix-based modern: E / N / Z / JD / J / MIJ / CIJ / V / SQ / JV / IC / CG / etc.
    const prefixMatch = sn.match(/^([A-Z]{1,3})(\d{2,6})$/);
    if(prefixMatch){
      const prefix = prefixMatch[1];
      const digits = prefixMatch[2];

      // E-series (Corona early 80s)
      if(/^E\d{1,5}$/.test(sn)){
        addCandidate(candidates, `E-prefix series • Corona-era (early 1980s)`, 'medium', 'E-prefix serials were used in early 80s Corona U.S. production. Digit counts/numeric ranges vary by year.');
        return candidates;
      }
      // N-series (1990s)
      if(/^N\d{2,6}$/.test(sn)){
        addCandidate(candidates, `N-prefix (1990s era) • pattern ${prefix}${digits}`, 'medium', 'N-prefixed serials were used mainly in the 1990s; check factory label for exact origin.');
        return candidates;
      }
      // Z-series (2000s)
      if(/^Z\d{1,6}$/.test(sn)){
        addCandidate(candidates, `Z-prefix (2000s era) • pattern ${prefix}${digits}`, 'medium', 'Z-prefixed serials indicate the 2000s decade transitions; check country/factory label.');
        return candidates;
      }
      // MIJ / CIJ / JD / J (Japan)
      if(/^(MIJ|CIJ|JD|J)$/.test(prefix)){
        addCandidate(candidates, `Japan-made prefix (${prefix})`, 'medium', 'Japan had multiple serial systems (MIJ, CIJ, JD, J). Model and factory matter — photos help refine.');
        return candidates;
      }
      // V-prefix (e.g., V + 6 digits) sometimes used for "Vintage" reissue / 80s/90s
      if(/^V\d{4,6}$/.test(sn)){
        addCandidate(candidates, `V-prefix (reissue/vintage-era) • pattern ${sn}`, 'low', 'V-prefixes are often used for reissue/modern runs; check model and production label.');
        return candidates;
      }
      // SQ / JV / Squier-related
      if(/^SQ|^JV/.test(prefix)){
        addCandidate(candidates, `${prefix} prefix (Squier/JV)`, 'low', 'Squier and JV models use manufacturer-specific schemes; consult model-specific tables for precision.');
        return candidates;
      }
      // IC/IS/CG/CO/CI prefixes (Indonesia/China/other Asia)
      if(/^(IC|IS|CG|CY|CI|CN|K|KC)$/.test(prefix)){
        addCandidate(candidates, `${prefix} prefix — Asia factory pattern`, 'medium', 'Factory-specific prefix — check headstock country stamp and build features.');
        return candidates;
      }
      // US handled earlier, but generic fallback:
      addCandidate(candidates, `Prefix ${prefix} • ${digits.length} digits`, 'low', 'Letter prefixes usually indicate factory/era. Provide photos or paperwork to refine the match.');
      return candidates;
    }

    // 9) Fallback: very long or unknown patterns
    addCandidate(candidates, 'Unknown or ambiguous format', 'low', 'Serial did not match quick patterns. Try removing punctuation or provide photos for manual review.');
    return candidates;
  }

  /* ---------- UI hookup ---------- */
  document.addEventListener('DOMContentLoaded', function () {
    const input = el('fender-serial-input');
    const btn = el('fenderLookupBtn');
    const out = el('fender-lookup-result');

    function showHTML(html){
      if(!out) return console.warn('Fender lookup: result container not found.');
      out.style.display = 'block';
      out.innerHTML = html;
      // wire toggle
      const toggle = out.querySelector('.fender-details-toggle');
      const collapse = out.querySelector('#fender-collapse');
      if(toggle && collapse){
        toggle.addEventListener('click', function () {
          const open = collapse.classList.toggle('open');
          toggle.textContent = open ? 'Hide details' : 'Show details';
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    }

    if(!input || !btn || !out){
      // Elements missing — silently fail but log
      console.error('Fender lookup engine: required elements missing (fender-serial-input, fenderLookupBtn, fender-lookup-result).');
      return;
    }

    function runLookup(){
      const raw = input.value || '';
      if(!raw.trim()){
        showHTML('<div style="color:#b00"><strong>Please enter a serial number.</strong></div>');
        return;
      }
      const candidates = lookupFenderSerial(raw);
      const html = formatCandidatesHTML(candidates);
      showHTML(html);
    }

    // run on Enter key
    input.addEventListener('keydown', function (ev) { if(ev.key === 'Enter'){ ev.preventDefault(); runLookup(); }});
    // run on click
    btn.addEventListener('click', runLookup);

    // optional: expose helper to window for debugging
    window.__gpg_fender_lookup = {
      lookup: lookupFenderSerial,
      format: formatCandidatesHTML
    };
  });

})(); // end closure
